import { useSyncExternalStore } from 'react';
import { streamChat, veraService } from './veraService';

/**
 * Module-level chat session store. The active Vera conversation — including an
 * in-flight streaming answer — lives OUTSIDE the React tree so navigating to
 * another page no longer drops the chat: the SSE stream keeps running, tokens
 * keep accumulating, and the finished turn is persisted server-side. When the
 * user returns to /vera the scene re-subscribes and picks up exactly where the
 * conversation is.
 *
 * Two snapshots are exposed:
 *  - the full snapshot for VeraScene (changes on every token), and
 *  - a tiny "activity" snapshot {busy, unseenDone} for the global sidebar,
 *    whose identity only changes when those flags flip — so the nav doesn't
 *    re-render per token while an answer streams.
 */

const state = {
  messages: [],       // {role, content, model?, error?, artifacts?, signals?, confidence?, serverId?, feedback?, streaming?, pendingCharts?}
  input: '',
  busy: false,
  unavailable: false,
  activeGuid: null,
  conversations: [],
  unseenDone: false,  // an answer reached a terminal state while the Vera screen wasn't mounted
};

let veraVisible = false;    // is VeraScene currently mounted?
let abortController = null;
const listeners = new Set();

let snapshot = { ...state };
let activity = { busy: state.busy, unseenDone: state.unseenDone };

const emit = () => {
  snapshot = { ...state };
  if (activity.busy !== state.busy || activity.unseenDone !== state.unseenDone) {
    activity = { busy: state.busy, unseenDone: state.unseenDone };
  }
  listeners.forEach((l) => { try { l(); } catch { /* subscriber gone */ } });
};

const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const getSnapshot = () => snapshot;
const getActivity = () => activity;

const patchLast = (patch) => {
  if (state.messages.length === 0) return;
  const copy = [...state.messages];
  const i = copy.length - 1;
  copy[i] = typeof patch === 'function' ? patch(copy[i]) : { ...copy[i], ...patch };
  state.messages = copy;
  emit();
};

const persistMessage = async (guid, msg) => {
  if (!guid) return null;
  try { return await veraService.appendMessage(guid, msg); } catch { return null; }
};

const ensureConversation = async (firstQuestion) => {
  if (state.activeGuid) return state.activeGuid;
  try {
    const c = await veraService.createConversation(firstQuestion?.slice(0, 80));
    state.activeGuid = c.guid;
    emit();
    veraChatStore.refreshConversations();
    return c.guid;
  } catch { return null; }
};

// Shared streaming runner — the LAST message is the assistant placeholder to
// stream into. Runs to completion regardless of whether the scene is mounted.
const runStream = async (q, history, context, guid) => {
  state.busy = true;
  emit();
  const controller = new AbortController();
  abortController = controller;
  // Accumulate the final turn in closure vars so persistence doesn't need to read state.
  let acc = '';
  let model; let artifacts = []; let signals; let confidence; let aborted = false;
  await streamChat({ message: q, history, context }, {
    signal: controller.signal,
    onToken: (t) => { acc += t; patchLast((m) => ({ ...m, content: acc })); },
    onArtifactPending: (count) => patchLast((m) => ({ ...m, pendingCharts: count || 0 })),
    onArtifacts: (arr) => { artifacts = arr || []; patchLast((m) => ({ ...m, artifacts, pendingCharts: 0 })); },
    onMeta: (meta) => { model = meta?.model; patchLast((m) => ({ ...m, model })); },
    onSignals: (s) => { signals = s?.signals || []; confidence = s?.confidence; patchLast((m) => ({ ...m, signals, confidence })); },
    onError: (e) => {
      const msg = e?.error || (e?.status === 503 ? 'unavailable' : '') || '';
      if (e?.name === 'AbortError') { aborted = true; return; }
      if (e?.status === 503 || String(msg).includes('unavailable')) { state.unavailable = true; emit(); }
      patchLast((m) => (m.content
        ? m
        : { ...m, error: true, content: (typeof msg === 'string' && msg && msg !== 'unavailable') ? msg : 'Vera is unavailable.' }));
    },
    onDone: async () => {
      state.busy = false;
      abortController = null;
      // Nudge the sidebar only when the answer finished (or failed) while the
      // user was elsewhere — never for user-initiated stops or on-screen turns.
      if (!veraVisible && !aborted) state.unseenDone = true;
      patchLast((m) => ({ ...m, streaming: false, pendingCharts: 0 }));
      emit();
      // Persist the finished assistant turn (server-side history + feedback target).
      if (guid && acc && !aborted) {
        const saved = await persistMessage(guid, {
          role: 'assistant', content: acc, model, artifacts, signals, confidence,
        });
        if (saved?.id) patchLast((m) => ({ ...m, serverId: saved.id }));
        veraChatStore.refreshConversations();
      }
    },
  });
};

export const veraChatStore = {
  subscribe,
  getSnapshot,
  getActivity,

  setInput(v) { state.input = v; emit(); },

  /** VeraScene mount/unmount. Mounting marks any pending completion as seen. */
  setVisible(v) {
    veraVisible = v;
    if (v && state.unseenDone) { state.unseenDone = false; emit(); }
  },

  async refreshConversations() {
    try { state.conversations = await veraService.listConversations(); emit(); } catch { /* ignore */ }
  },

  send(text, context) {
    const q = (text ?? state.input).trim();
    if (!q || state.busy) return;
    this.doSend(q, context);
  },

  async doSend(q, context) {
    state.input = '';
    state.unavailable = false;
    const history = state.messages.filter((m) => !m.error).slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));
    state.messages = [...state.messages,
      { role: 'user', content: q },
      { role: 'assistant', content: '', streaming: true, artifacts: [] }];
    emit();
    const guid = await ensureConversation(q);
    await persistMessage(guid, { role: 'user', content: q });
    await runStream(q, history, context, guid);
  },

  /** "Ask Vera about this" deep links always start a fresh conversation. */
  deepLink(q, context) {
    this.newChat();
    this.send(q, context);
  },

  async regenerate(i) {
    if (state.busy) return;
    const userMsg = state.messages[i - 1];
    if (!userMsg || userMsg.role !== 'user') return;
    state.unavailable = false;
    const history = state.messages.slice(0, i - 1).filter((m) => !m.error).slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));
    state.messages = [...state.messages.slice(0, i),
      { role: 'assistant', content: '', streaming: true, artifacts: [] }];
    emit();
    await runStream(userMsg.content, history, undefined, state.activeGuid);
  },

  stopStreaming() {
    try { abortController?.abort(); } catch { /* noop */ }
    abortController = null;
    state.busy = false;
    if (state.messages.length > 0) {
      const copy = [...state.messages];
      const i = copy.length - 1;
      const m = copy[i];
      copy[i] = m.streaming ? { ...m, streaming: false, content: m.content || '_(stopped)_' } : m;
      state.messages = copy;
    }
    emit();
  },

  newChat() {
    if (state.busy) this.stopStreaming();
    state.messages = [];
    state.activeGuid = null;
    state.unavailable = false;
    emit();
  },

  async openChat(guid) {
    if (state.busy) this.stopStreaming();
    state.unavailable = false;
    state.activeGuid = guid;
    emit();
    try {
      const data = await veraService.getConversation(guid);
      if (state.activeGuid !== guid) return; // user switched again mid-load
      state.messages = (data.messages || []).map((m) => ({
        role: m.role, content: m.content, model: m.model, artifacts: m.artifacts || [],
        signals: m.signals || undefined, confidence: m.confidence, serverId: m.id, feedback: m.feedback || 0,
      }));
    } catch {
      if (state.activeGuid === guid) state.messages = [];
    }
    emit();
  },

  async deleteConversation(guid) {
    try { await veraService.deleteConversation(guid); } catch { /* ignore */ }
    if (guid === state.activeGuid) this.newChat();
    this.refreshConversations();
  },

  async setFeedback(i, value) {
    const m = state.messages[i];
    if (!m?.serverId) return;
    const next = m.feedback === value ? 0 : value; // toggle off if same
    state.messages = state.messages.map((x, idx) => (idx === i ? { ...x, feedback: next } : x));
    emit();
    try { await veraService.sendFeedback(m.serverId, next); } catch { /* best-effort */ }
  },
};

/** Full session snapshot — for VeraScene. Re-renders per streamed token. */
export const useVeraChat = () => useSyncExternalStore(subscribe, getSnapshot);

/** {busy, unseenDone} only — for the global sidebar icon. Stable identity per token. */
export const useVeraActivity = () => useSyncExternalStore(subscribe, getActivity);

export default veraChatStore;
